import React from 'react'
import { useApolloClient } from 'react-apollo'
import MediaQuery from 'react-responsive'
import { withRouter } from 'react-router-dom'
import type { RouteComponentProps } from 'react-router-dom'

import { Ink, Item, Menu, Overlay } from '@acorns/web-components'
import {
  useRemoteStrings,
  withAnalyticsClient,
  withEnv,
} from '@acorns/web-utils'
import type { Environment, GenericAnalyticsClient } from '@acorns/web-utils'
import { compose, withHandlers, withStateHandlers } from 'recompose'
import styled from 'styled-components'

import ChevronDownPath from 'assets/icons/chevron-down.svg'
import { useAIAssistant } from 'components/AIAssistantComponent/hooks/useAIAssistant'
import withClickOutside from 'components/HOCs/withClickOutside'
import TouchableOpacity from 'components/TouchableOpacity'
import { Subheading } from 'components/typography'
import UserImage, { Size } from 'components/UserImage'
import {
  useBenefitsHubUserTierLazyQuery,
  useReferralPillQuery,
} from 'generated/types'
import { useUserHasBenefitsHub } from 'pages/benefits-hub/hooks/useUserHasBenefitsHub'
import { routes } from 'router/routes'
import themeValues, { style } from 'theme'
import { logout } from 'utils/session'
import {
  BENEFITS_HUB_USER_DROPDOWN,
  LOGOUT,
  MENU_ACCESSIBILITY_LINK,
  MENU_INVITE_LINK,
  MENU_MESSAGE_CENTER_LINK,
  MENU_REDEEM_LINK,
  MENU_SETTINGS_LINK,
  USER_DROPDOWN_BUTTON,
  USER_DROPDOWN_MENU,
  USER_HELP_BUTTON,
} from 'utils/testing/testIDs'

import {
  isEqualWebScriptLoaded,
  loadEqualWebScript,
  showEqualWebWidget,
} from '../../vendors/equalweb'
import {
  ACTIONS,
  BENEFITS_HUB_NAV_MENU_TAPPED,
  screenReopenConfirmationLogout,
  screenSubscriptionCancelledLogout,
} from './events'

type Props = {
  closedAt?: string
  email?: string
  uuid?: string
  firstName?: string
  lastName?: string
  toggles?: any
  messageCount: number
  refetchMessages: () => void
}

type WithEnvProps = {
  env: Environment
}

type WithAnalyticsClientProps = {
  analytics: GenericAnalyticsClient
}

type WithRouterProps = RouteComponentProps

type StateProps = {
  isOpen: boolean
}

type HandlerProps = {
  handleClick: () => void
  handleClickOutside: () => void
  handleGiftCardClick: () => void
  handleInviteFriendsClick: () => void
  handleMessageCenterClick: () => void
  handleSettingsClick: () => void
  handleOpenAccessibilityClick: () => void
  handleBenefitsHubClick: () => void
  handleFinancialAdvisorClick: () => void
}

type EnhancedProps = Props &
  HandlerProps &
  StateProps &
  WithEnvProps &
  WithAnalyticsClientProps &
  WithRouterProps

const Container = styled.div`
  display: flex;
  margin: 0 10px;
  padding: 20px;
  position: relative;

  @media screen and (max-width: ${style((theme) => theme.breakpoints.md)}px) {
    margin: 0;
  }
`

const Trigger = styled(TouchableOpacity)`
  align-items: center;
  display: flex;
  height: 32px;
` as any as React.FC<{ onClick: () => void }>

const UserImageStyled = styled(UserImage)`
  border-width: 0;
  box-sizing: content-box;
  height: 32px;
  width: 32px;

  @media screen and (max-width: ${style((theme) => theme.breakpoints.md)}px) {
    height: 28px;
    width: 28px;
  }
` as React.FC<{ email?: string; size: Size }>

const Chevron = styled.img`
  margin-left: 8px;

  @media screen and (max-width: ${style((theme) => theme.breakpoints.md)}px) {
    display: none;
  }
`

const Pill = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 23px;
  border-radius: 11px;
  font-size: 12px;
  padding: 2px 8px;
  background-color: ${style((theme) => theme.colors.purple)};
`

const StyledSubHeading = styled(Subheading)`
  color: ${style((theme) => theme.colors.white)};
  text-transform: capitalize;
`

const enhance = compose<EnhancedProps, Props>(
  withEnv,
  withRouter,
  withAnalyticsClient,
  withStateHandlers<StateProps, any>(
    {
      isOpen: false,
    },
    {
      handleSetOpen: () => (currentlyOpen: boolean) => ({
        isOpen: currentlyOpen,
      }),
      handleClickOutside: () => () => ({
        isOpen: false,
      }),
    },
  ),
  withHandlers<any, Partial<HandlerProps>>({
    handleClick:
      ({ handleSetOpen, isOpen, refetchMessages }) =>
      async () => {
        if (!isOpen) {
          await refetchMessages()
        }
        handleSetOpen(!isOpen)
      },
    handleSettingsClick:
      ({ history }) =>
      () =>
        history.push('/settings'),
    handleGiftCardClick:
      ({ history }) =>
      () =>
        history.push('/redeem'),
    handleInviteFriendsClick:
      ({ history }) =>
      () =>
        history.push('/invite'),
    handleBenefitsHubClick:
      ({ analytics, history }) =>
      () => {
        analytics.track(ACTIONS.BUTTON_TAPPED, BENEFITS_HUB_NAV_MENU_TAPPED)
        history.push(routes.benefitsHub.index, {
          benefitsHubOrigin: 'menu',
        })
      },
    handleOpenAccessibilityClick: () => () => {
      if (isEqualWebScriptLoaded()) {
        showEqualWebWidget()
      } else {
        loadEqualWebScript()
        const checkEqualWebLoadedInterval = setInterval(() => {
          if (isEqualWebScriptLoaded()) {
            clearInterval(checkEqualWebLoadedInterval)
            showEqualWebWidget()
          }
        }, 500)
      }
    },
    handleMessageCenterClick:
      ({ history }) =>
      () =>
        history.push(routes.alerts.index),
    handleFinancialAdvisorClick:
      ({ history }) =>
      () =>
        history.push(routes.financialAdvisor.index),
  }),
  withClickOutside(
    ({ handleClickOutside }: Pick<HandlerProps, 'handleClickOutside'>) => {
      handleClickOutside()
    },
  ),
)

const UserDropdown = (props: EnhancedProps) => {
  const { data: referralPillData } = useReferralPillQuery()
  const { getRemoteString } = useRemoteStrings()
  const { hasBenefitsHub } = useUserHasBenefitsHub()
  const { handleOpenAssistant } = useAIAssistant()

  // refresh useUserHasBenefitsHub data in case of user changing tier
  const [refetchBenefitsHub] = useBenefitsHubUserTierLazyQuery({
    fetchPolicy: 'cache-and-network',
  })
  const remoteStrings = {
    benefitsHubTitle: getRemoteString({
      key: 'navigation_drawer.profile.row.family_benefits.title',
    }),
    benefitsHubPill: getRemoteString({
      key: 'navigation_drawer.profile.row.family_benefits.pill',
    }),
  }

  const apolloClient = useApolloClient()

  const handleLogoutClick = async () => {
    // clear session for Salesforce embedded service
    if (window.embeddedservice_bootstrap) {
      await window.embeddedservice_bootstrap?.userVerificationAPI?.clearSession(
        true,
      )
    }

    const { history, closedAt, analytics } = props
    // track log out click coming from the closed account screens
    const pathName = window.location.pathname
    if (pathName.startsWith('/closed-account')) {
      analytics.track(
        ACTIONS.BUTTON_TAPPED,
        pathName.endsWith('/reopen')
          ? screenReopenConfirmationLogout()
          : screenSubscriptionCancelledLogout(),
      )
    }

    if (closedAt) {
      await logout(apolloClient)
      window.location.assign('/')
    } else {
      history.push('/logout')
    }
  }

  const handleTriggerClick = () => {
    props.handleClick()
    if (!props.isOpen) {
      refetchBenefitsHub()
    }
  }

  const referralPillValue =
    referralPillData?.advocate.experienceContent?.pillAmount &&
    `$${referralPillData?.advocate.experienceContent?.pillAmount}`

  return (
    <Container>
      <Trigger data-testid={USER_DROPDOWN_BUTTON} onClick={handleTriggerClick}>
        <UserImageStyled email={props.email} size={Size.MD} />
        <Chevron src={ChevronDownPath} />
      </Trigger>

      {props.isOpen && (
        <>
          <MediaQuery maxWidth={themeValues.breakpoints.md - 1}>
            <Overlay onClick={props.handleClickOutside} />
          </MediaQuery>

          <Menu
            onClick={props.handleClickOutside}
            data-testid={USER_DROPDOWN_MENU}
          >
            {!props.closedAt && (
              <Item
                onClick={props.handleSettingsClick}
                data-testid={MENU_SETTINGS_LINK}
              >
                Profile & Settings
                <Ink opacity={0.02} />
              </Item>
            )}
            {!props.closedAt && (
              <Item
                onClick={props.handleMessageCenterClick}
                data-testid={MENU_MESSAGE_CENTER_LINK}
              >
                Message Center
                {props.messageCount > 0 && (
                  <Pill>
                    <StyledSubHeading>{props.messageCount}</StyledSubHeading>
                  </Pill>
                )}
              </Item>
            )}
            {hasBenefitsHub && (
              <Item
                onClick={props.handleBenefitsHubClick}
                data-testid={BENEFITS_HUB_USER_DROPDOWN}
              >
                {remoteStrings.benefitsHubTitle}
                <Pill>
                  <StyledSubHeading>
                    {remoteStrings.benefitsHubPill}
                  </StyledSubHeading>
                </Pill>
              </Item>
            )}
            {!props.closedAt && referralPillValue && (
              <Item
                onClick={props.handleInviteFriendsClick}
                data-testid={MENU_INVITE_LINK}
              >
                Invite Friends
                <Pill>
                  <StyledSubHeading>Get {referralPillValue}</StyledSubHeading>
                </Pill>
                <Ink opacity={0.02} />
              </Item>
            )}
            {!props.closedAt && (
              <Item
                onClick={props.handleGiftCardClick}
                data-testid={MENU_REDEEM_LINK}
              >
                Redeem Gift Card
                <Ink opacity={0.02} />
              </Item>
            )}
            {!props.closedAt && (
              <Item
                onClick={handleOpenAssistant}
                data-testid={USER_HELP_BUTTON}
              >
                Get Help
                <Pill>
                  <StyledSubHeading>New</StyledSubHeading>
                </Pill>
                <Ink opacity={0.02} />
              </Item>
            )}
            <Item
              onClick={props.handleOpenAccessibilityClick}
              data-testid={MENU_ACCESSIBILITY_LINK}
            >
              Accessibility
              <Ink opacity={0.02} />
            </Item>
            <Item onClick={handleLogoutClick} danger data-testid={LOGOUT}>
              Log Out
              <Ink opacity={0.02} />
            </Item>
          </Menu>
        </>
      )}
    </Container>
  )
}

export default enhance(UserDropdown)
